import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, BookOpen, Building2, MapPin, Map, FileText, LifeBuoy, Package, Layers, Newspaper, Compass, Mic, MicOff, X, Clock, TrendingUp, Home, LayoutDashboard, MessageCircle, Eye, ShoppingBag } from 'lucide-react';
import { resolveIntent } from '../../data/intentRouting';
import { searchAll } from '../../data/searchEngine';
import { quickSuggestions } from '../../data/intentRouting';
import { searchMatchers } from '../recommendations/matcherSearchIndex';
import MiniMatcher from '../recommendations/MiniMatcher';
import BuildTypePopup from '../recommendations/BuildTypePopup';
import useIsSAContext from '../../hooks/useIsSAContext';
import { useIsSA, useUserState } from '../../lib/userState';
import useSessionContact from '../../hooks/useSessionContact';
import useAnonymousBrief from '../../hooks/useAnonymousBrief';
import { buildingTypes } from '../../data/mock';
import {
  highlightSegments,
  getRecentSearches,
  pushRecentSearch,
  clearRecentSearches,
  logSearchQuery,
  fetchTrendingSearches,
  supportsVoiceInput,
  startVoiceInput,
} from '../../utils/searchUx';
import { useAuthModal } from '../../context/AuthModalContext';
import { useMarketingAuth } from '../../hooks/useMarketingAuth';
import { findSponsoredAnswer } from '../../workspace/services/advertisingService';
import SponsoredSearchAnswer from './SponsoredSearchAnswer';
import { resolveProductSuggestion } from '../../data/productSearch';

// Render text with matched-segment highlighting.
const HighlightedText = ({ text, query, className = '' }) => {
  const segments = useMemo(() => highlightSegments(text, query), [text, query]);
  return (
    <span className={className}>
      {segments.map((seg, i) =>
        seg.match ? (
          <mark key={i} className="bg-[#febd59]/40 text-[#1d1d1f] font-semibold rounded-sm">
            {seg.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        )
      )}
    </span>
  );
};

// 36x36 branded logo tile for `business` search hits. Always uses a
// white plate + subtle border when we have a logo image (so a wide
// wordmark isn't cropped and the brand's own colors show through
// cleanly). Only falls back to a coloured plate + business initials
// when the image is missing or fails to load.
const BusinessLogoTile = ({ title, logoUrl, brandColor }) => {
  const [broken, setBroken] = useState(false);
  const initials = useMemo(() => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  }, [title]);
  const showImg = logoUrl && !broken;
  if (showImg) {
    return (
      <div
        className="w-9 h-9 shrink-0 rounded-md overflow-hidden flex items-center justify-center mt-0.5 bg-white border border-gray-200"
        aria-hidden="true"
      >
        <img
          src={logoUrl}
          alt=""
          className="w-full h-full object-contain p-1"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      </div>
    );
  }
  // Fallback: coloured plate with initials in a contrasting colour.
  const bg = brandColor || '#1d1d1f';
  const isLightPlate = (() => {
    const hex = bg.replace('#', '');
    if (hex.length !== 6) return false;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 200;
  })();
  return (
    <div
      className={`w-9 h-9 shrink-0 rounded-md flex items-center justify-center mt-0.5 ${isLightPlate ? 'border border-gray-200' : ''}`}
      style={{ backgroundColor: bg }}
      aria-hidden="true"
    >
      <span className={`text-[11px] font-bold tracking-tight leading-none ${isLightPlate ? 'text-[#1d1d1f]' : 'text-white'}`}>
        {initials}
      </span>
    </div>
  );
};

// Per-kind metadata for the dropdown - icon + badge label + colour.
// Kept in one place so adding a new kind to the index only needs a
// single new entry here for it to render consistently.
const KIND_META = {
  compound:           { icon: Compass,  label: 'Best match',  accent: '#a55c2a' },
  glossary:           { icon: BookOpen,  label: 'Glossary',    accent: '#1d8efe' },
  // Insider "What the pros see" reading guide - distinct badge + Eye
  // icon so it reads as a pro pick, not a generic page.
  pros:               { icon: Eye,       label: 'What the pros see', accent: '#0f5aa6' },
  builder:            { icon: Building2, label: 'Builder',     accent: '#1d1d1f' },
  suburb:             { icon: MapPin,    label: 'Suburb',      accent: '#1f7a4f' },
  estate:             { icon: Map,       label: 'Estate',      accent: '#1f7a4f' },
  'case-study':       { icon: Newspaper, label: 'Real story',  accent: '#a55c2a' },
  help:               { icon: LifeBuoy,  label: 'Help',        accent: '#a55c2a' },
  supplier:           { icon: Package,   label: 'Supplier',    accent: '#1d8efe' },
  'supplier-category':{ icon: Layers,    label: 'Suppliers',   accent: '#1d8efe' },
  page:               { icon: FileText,  label: 'Page',        accent: '#86868b' },
  // Workspace tools render their own 3D illustration as the row icon
  // (handled separately in the suggestion renderer below) so the
  // lucide-react icon here is only used as a fallback.
  'workspace-tool':   { icon: LayoutDashboard, label: 'Dashboard tool', accent: '#f5a623' },
  // MiniMatcher quick-action, pinned to the top of the dropdown
  // and rendered with the BuildPilot blue accent. Clicking opens
  // the popup (or navigates to the full builder wizard for the
  // builder category).
  matcher:            { icon: MessageCircle, label: 'Get matched', accent: '#1d8efe' },
  // 2026-feb: Live Supabase catalog hits - H&L packages, floor-plan
  // designs, and vacant land blocks. Fetched async via the
  // /api/search/catalog endpoint and merged into the dropdown so the
  // search bar truly behaves like "Google for building".
  package:            { icon: Home,       label: 'H&L package', accent: '#1d8efe' },
  design:             { icon: Layers,     label: 'Floor plan',  accent: '#7c3aed' },
  land:               { icon: Map,        label: 'Land block',  accent: '#1f7a4f' },
  // Live business hit from `/api/search/catalog` (Supabase + file-
  // store onboarded builders / tradies / suppliers / professionals).
  // Renders with the workspace-tool visual treatment - 36x36 logo
  // thumbnail to the left of the business name.
  business:           { icon: Building2,  label: 'Business',    accent: '#1d1d1f' },
  // Full-platform coverage kinds (extraContentIndex.js)
  question:           { icon: LifeBuoy,   label: 'Question',    accent: '#a55c2a' },
  guide:              { icon: BookOpen,   label: 'Guide',       accent: '#1d8efe' },
  melbourne:          { icon: MapPin,     label: 'Melbourne',   accent: '#00a550' },
  'suburb-guide':     { icon: MapPin,     label: 'Suburb guide',accent: '#1f7a4f' },
  'design-collection':{ icon: Layers,     label: 'Design collection', accent: '#7c3aed' },
  'project-type':     { icon: Compass,    label: 'Project type',accent: '#a55c2a' },
};

// Decision-signal keywords - when present in a query, the bar surfaces a
// soft contextual prompt to talk to a real human. Tuned to fire on roughly
// 1 in 15 searches: only on actual decision/hesitation language, never on
// browse-style queries like "metricon" or "double storey".
const DECISION_KEYWORDS = [
  'should i', 'should we', 'help me decide', 'help me choose', 'which is better',
  'which builder', 'who should', 'stuck', 'confused', 'not sure', 'unsure',
  'first home', 'first build', 'first time', 'contract', 'review my',
  'red flag', 'red flags', 'is it worth', 'is this a good', 'good builder',
  'compare quote', 'compare quotes', 'quote review', 'before signing',
  'sign contract', 'before i sign', 'worried', 'scared', 'trust',
  'overwhelmed', 'too much', 'feel like',
];

// Situation-aware rotating placeholder hints. Each set's FIRST entry is
// the "primary" framing line (rendered without the "Try: " prefix); the
// rest rotate with the prefix. They're purely visual nudges - the user's
// real query is routed by `resolveIntent` - but tuned to the content we
// actually ship so they entice the next obvious action.
//
// The active set is chosen from the visitor's journey stage
// (bp:lastLifecycleStage, broadcast live via the `buildpilot:stage-changed`
// event) and their state (SA → Adelaide, VIC → Melbourne) via {CITY}.
const HINT_SETS = {
  default: [
    "Builders, costs & suburbs",
    "Build for $500k?",
    "Builders in {CITY}",
    "House & land near me",
    "How long to build?",
    "Renovate or rebuild?",
    "Is my builder any good?",
  ],
  dream: [
    "Picture your future home",
    "Browse 760+ floor plans",
    "Single or double storey?",
    "Build for $500k?",
    "Display homes near me",
    "Facade ideas to save",
    "Build, renovate or buy?",
  ],
  plan: [
    "Plan your build budget",
    "Build for $600k?",
    "Cheap land in {CITY}",
    "$500k house & land",
    "How much to build?",
    "Top suburbs in {CITY}",
    "How long to build?",
  ],
  choose: [
    "Find the right builder",
    "Top builders in {CITY}",
    "Shortlist a builder fast",
    "Questions for a builder",
    "Volume vs custom build?",
    "Check builder licence",
    "Compare my quotes",
  ],
  build: [
    "Keep your build on track",
    "Track my build budget",
    "Scan for home defects",
    "Builder's gone quiet?",
    "Find an inspector",
    "Common builder problems",
    "What's a variation?",
  ],
  own: [
    "Love where you live",
    "Fix a handover defect",
    "Find a tradie near you",
    "Add a granny flat",
    "Energy-efficient reno",
    "Renovate or rebuild?",
    "Do it all again?",
  ],
};

const cityFor = (stateCode) =>
  stateCode === 'SA' ? 'Adelaide' : stateCode === 'VIC' ? 'Melbourne' : 'your area';

const buildHints = (stageSlug, stateCode) => {
  const set = HINT_SETS[stageSlug] || HINT_SETS.default;
  const city = cityFor(stateCode);
  return set.map((h) => h.replace(/\{CITY\}/g, city));
};

/**
 * Intent-driven search bar. Static dictionary routing (no full-text search).
 * variant="hero" = large, light-on-dark hero version (homepage)
 * variant="header" = compact header version
 * variant="inline" = neutral page placement
 *
 * If `placeholder` is omitted on the hero variant, we cycle through a
 * situation-aware hint set (chosen from the visitor's journey stage +
 * state) every 2.4s with a "Try: " prefix.
 */
const IntentSearchBar = ({ variant = "hero", placeholder, autoFocus = false, onNavigate }) => {
  const navigate = useNavigate();
  const authModal = useAuthModal();
  const marketingAuth = useMarketingAuth();
  const { isSA } = useIsSAContext();
  const saConfirmed = useIsSA();
  // Situation signal for the placeholder: visitor's state (SA/VIC) and
  // their current journey stage (read once on mount, kept live via the
  // `buildpilot:stage-changed` broadcast from the lifecycle picker).
  const placeholderState = useUserState();
  const [stageSlug, setStageSlug] = useState(() => {
    try { return window.localStorage.getItem('bp:lastLifecycleStage') || null; }
    catch { return null; }
  });
  useEffect(() => {
    const onStage = (e) => setStageSlug(e?.detail || null);
    window.addEventListener('buildpilot:stage-changed', onStage);
    return () => window.removeEventListener('buildpilot:stage-changed', onStage);
  }, []);
  const activeHints = useMemo(
    () => buildHints(stageSlug, placeholderState),
    [stageSlug, placeholderState]
  );
  const { contact: sessionContact } = useSessionContact();
  const { brief: anonBrief } = useAnonymousBrief();
  // Known caller state (from session contact first, then anon brief).
  // Passed to /api/search/catalog so businesses whose service_states
  // don't cover the caller are excluded (starting with House Inspect
  // Australia, whose overlay is SA-only). Undefined means "no known
  // state" - the endpoint then returns unrestricted results.
  const knownUserState = useMemo(() => {
    const s = (sessionContact?.state || anonBrief?.state || '').toUpperCase().trim();
    return s || null;
  }, [sessionContact?.state, anonBrief?.state]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [hintIdx, setHintIdx] = useState(0);
  const [recents, setRecents] = useState([]);
  const [trending, setTrending] = useState([]);
  const [voiceOn, setVoiceOn] = useState(false);
  const [packageSuburbs, setPackageSuburbs] = useState([]);
  const [packageRegions, setPackageRegions] = useState([]);
  const [displayHomeSuburbs, setDisplayHomeSuburbs] = useState([]);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const recogRef = useRef(null);

  const useRotating = !placeholder;
  const isPrimaryHint = useRotating && hintIdx === 0;
  const currentHint = activeHints[hintIdx] || activeHints[0];
  const activePlaceholder = useRotating
    ? (isPrimaryHint ? currentHint : `Try: ${currentHint}`)
    : placeholder;
  const [focused, setFocused] = useState(false);
  const showAnimatedHint = useRotating && !query && !focused;

  // When the user has typed something we run the unified search.
  // When the input is empty we show recents + trending + popular.
  const baseSuggestions = query
    ? searchAll(query, { limit: 8 })
    : quickSuggestions.slice(0, 6).map((s) => ({ kind: 'page', title: s.label, to: s.to }));

  // 2026-feb: Live catalog typeahead. Debounced 250ms fetch against
  // /api/search/catalog so the dropdown surfaces real packages,
  // floor-plan designs and vacant land blocks as the user types -
  // not just static intent/page hits.
  const [liveCatalogHits, setLiveCatalogHits] = useState([]);
  useEffect(() => {
    const q = (query || '').trim();
    if (q.length < 2) { setLiveCatalogHits([]); return undefined; }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const stateParam = knownUserState ? `&state=${encodeURIComponent(knownUserState)}` : '';
        const r = await fetch(`${base}/api/search/catalog?q=${encodeURIComponent(q)}&limit=8${stateParam}`, { signal: ctl.signal });
        if (!r.ok) return;
        const data = await r.json();
        const results = Array.isArray(data?.results) ? data.results : [];
        // Map backend → suggestion shape. The thumbnail is piped
        // through `iconUrl` so the row reuses the workspace-tool
        // visual treatment (36×36 image to the left of the title).
        // We bump score above the static MIN_SCORE (22) so a real
        // catalog hit always beats a stale glossary token-match.
        setLiveCatalogHits(results.map((r2) => ({
          kind: r2.kind,        // 'package' | 'design' | 'land' | 'business'
          title: r2.title,
          subtitle: [r2.subtitle, r2.price_band].filter(Boolean).join(' · '),
          snippet: (r2.specs && r2.specs.length) ? r2.specs.join(' · ') : '',
          iconUrl: r2.thumbnail || null,
          // Business-specific fields.
          brandColor: r2.brand_color || null,
          logoPlateStyle: r2.logo_plate_style || null,
          // Overrides the generic "BUSINESS" badge with a specific
          // subcategory label ("Builder", "Renovation Builder",
          // "Bricklayer", ...) so users can scan the shortlist.
          badge: r2.badge || null,
          to: r2.to,
          // Strong catalog hits stay pinned above content; weak fuzzy
          // hits (backend score < 20) sit with the static results so a
          // named page can outrank them.
          score: ((r2.score || 0) >= 20 ? 120 : 60) + (r2.score || 0),
        })));
      } catch (e) {
        if (e?.name !== 'AbortError') {
          // swallow - search will fall back to static hits
        }
      }
    }, 250);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [query, knownUserState]);

  // Matchers, surface up to 3 contextual MiniMatcher quick-actions
  // ABOVE the regular index results. e.g. typing "surveyor" surfaces
  // "Find a Surveyor · Professional", or typing "renovation"
  // surfaces "Renovating your home · Build type" which opens the
  // BuildTypePopup instead of a single MiniMatcher.
  const matcherHits = query
    ? searchMatchers(query, { limit: 3, isSA }).map((m) => ({
        kind: m.kind === 'build_type' ? 'build_type' : 'matcher',
        title: m.label,
        tag: m.tag,
        score: 200 + m.score,   // pinned above content results
        category: m.category || null,
        linkTo: m.linkTo || null,
        buildType: m.buildType || null,
      }))
    : [];
  // Normalize a business/builder title so we can dedupe across
  // sources (live catalog + static content index). Strips state
  // suffixes so "Simonds Homes" and "Simonds Homes SA" fold into
  // one hit. Keep this in lock-step with backend `_normalize_business_name`.
  const normalizeBusinessTitle = (s) => {
    if (!s) return '';
    let t = String(s).trim().toLowerCase();
    const legal = [' pty ltd', ' pty. ltd.', ' pty ltd.', ' pty limited', ' ltd', ' limited', ' inc', ' plc'];
    for (const suf of legal) if (t.endsWith(suf)) { t = t.slice(0, -suf.length).trim(); break; }
    const states = [' sa', ' vic', ' nsw', ' qld', ' wa', ' tas', ' act', ' nt',
                    ' south australia', ' victoria', ' new south wales', ' queensland',
                    ' western australia', ' tasmania', ' northern territory'];
    for (const suf of states) if (t.endsWith(suf)) { t = t.slice(0, -suf.length).trim(); break; }
    return t.replace(/\s+/g, ' ');
  };

  // Build a set of normalized business titles from the live catalog
  // so we can suppress the STATIC builder index hits that duplicate
  // them (e.g. contentIndex's "Simonds Homes / BUILDER" tile with no
  // logo is redundant when the live catalog already surfaces the
  // same Simonds tile with its real brand logo).
  const liveBusinessTitles = useMemo(() => {
    const set = new Set();
    for (const h of liveCatalogHits) {
      if (h.kind === 'business' && h.title) {
        set.add(normalizeBusinessTitle(h.title));
      }
    }
    return set;
  }, [liveCatalogHits]);

  const filteredBaseSuggestions = useMemo(() => (
    baseSuggestions.filter((s) => {
      if (s.kind === 'builder' && s.title && liveBusinessTitles.has(normalizeBusinessTitle(s.title))) {
        return false;
      }
      return true;
    })
  ), [baseSuggestions, liveBusinessTitles]);

  // Sponsored answer at the top of the dropdown - relevance-matched
  // to the query + viewer state, probabilistically shown (~40% of the
  // time when a match exists) so it doesn't feel like an ad-per-query.
  // Debounced by 220ms so we don't spam the service on every keystroke.
  const [sponsoredAnswer, setSponsoredAnswer] = useState(null);
  useEffect(() => {
    const q = (query || '').trim();
    if (q.length < 3) { setSponsoredAnswer(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const ad = await findSponsoredAnswer(q);
        if (!cancelled) setSponsoredAnswer(ad);
      } catch (_) { /* ignore */ }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  // Stable random slot for the sponsored answer: index 2, 3 or 4 —
  // never pinned to the top, never at the very bottom. Derived from
  // the query hash so the same query in the same session slots the
  // ad at the same row (no flicker as the user pauses on a word).
  const adInsertIndex = useMemo(() => {
    if (!sponsoredAnswer) return -1;
    const total = matcherHits.length + liveCatalogHits.length + filteredBaseSuggestions.length;
    if (total < 3) return -1;
    const q = (query || '').toLowerCase();
    let h = 0;
    for (let i = 0; i < q.length; i += 1) h = (h * 31 + q.charCodeAt(i)) >>> 0;
    const maxPos = Math.min(total - 1, 4);
    return 2 + (h % Math.max(1, maxPos - 1));
  }, [sponsoredAnswer, query, matcherHits.length, liveCatalogHits.length, filteredBaseSuggestions.length]);

  const suggestions = [...matcherHits, ...liveCatalogHits, ...filteredBaseSuggestions]
    // Keep matcher hits at the very top (highest score), then live
    // catalog, then static content - sort stably by score with the
    // matcher/catalog pin bonuses already applied above.
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, 12);

  const noResults = query.length >= 2 && suggestions.every((s) => s.kind === 'page' && s.score === 35);

  // Trigger 1 - decision-language detection. Fires only on hesitation/decision
  // queries (e.g. "should i sign", "first home", "review my contract"). Surfaces
  // a soft One on One Support tile at the TOP of results.
  const showDecisionPrompt = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 4) return false;
    return DECISION_KEYWORDS.some((k) => q.includes(k));
  }, [query]);

  // Product-intent detection - when the query reads like a physical product our
  // affiliate partners stock, surface a "See products" tile into /products.
  const productSuggestion = useMemo(() => resolveProductSuggestion(query), [query]);

  // Trigger 2 - compound queries (location + category, e.g. "builder in Munno
  // Para") are decision-paralysis queries by definition. Surface a soft
  // "want a hand?" card AFTER the results. Skip if no-results so we don't
  // double up with the existing no-results CTA bar.
  const showCompoundPrompt = useMemo(
    () => !noResults && suggestions.some((s) => s.kind === 'compound'),
    [suggestions, noResults]
  );

  // Lazy-load the live package-suburb index once on mount. Small payload,
  // cached server-side, so this is cheap. Used to surface live H&L matches.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/packages/suburbs`);
        if (!r.ok) return;
        const j = await r.json();
        if (alive) {
          setPackageSuburbs(j.suburbs || []);
          setPackageRegions(j.regions || []);
        }
      } catch (_) { /* silent - feature degrades gracefully */ }
    })();
    return () => { alive = false; };
  }, []);

  // Lazy-load the display-home suburb index once on mount (cheap, cached).
  // Powers the "display homes near {suburb}" search tile.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/home-designs/display-homes/suburbs`);
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setDisplayHomeSuburbs(j.suburbs || []);
      } catch (_) { /* silent - feature degrades gracefully */ }
    })();
    return () => { alive = false; };
  }, []);
  //   a) Query contains a known suburb name      → suggest that suburb
  //   b) Query is a generic intent phrase only   → suggest the hub
  const packageSuggestion = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 3 || packageSuburbs.length === 0) return null;

    // Normalise common Adelaide-suburb abbreviations so "mt barker" matches
    // "Mount Barker", "p adel" matches "Port Adelaide", etc.
    const qNorm = q
      .replace(/\bmt\b/g, 'mount')
      .replace(/\bst\b/g, 'saint')
      .replace(/\bn\b/g, 'north')
      .replace(/\bs\b/g, 'south');

    // 1) Try to find an actual suburb name inside the query. Sort by name
    //    length descending so longer suburb names match before shorter ones
    //    (e.g. "Munno Para Downs" beats "Munno Para").
    const sortedByLength = [...packageSuburbs].sort(
      (a, b) => b.suburb.length - a.suburb.length
    );
    const suburbMatch = sortedByLength.find((s) => {
      const name = s.suburb.toLowerCase();
      if (name.length < 4) return false;
      return q.includes(name) || qNorm.includes(name);
    });
    if (suburbMatch) {
      return {
        kind: 'package_suburb',
        suburb: suburbMatch.suburb,
        suburb_slug: suburbMatch.suburb_slug,
        count: suburbMatch.count,
        to: `/house-and-land-adelaide/${suburbMatch.suburb_slug}`,
      };
    }

    // 2) Generic H&L intent phrase with no suburb mentioned → link to hub.
    //    Count must reflect Adelaide-area inventory only (Adelaide + Adelaide
    //    Hills), NOT the full feed which also includes Melbourne / Geelong /
    //    Regional VIC. The total + other-regions tail is exposed separately so
    //    the UI can honestly say "55 in Adelaide · plus 37 across Melbourne, ...".
    const isGenericIntent =
      /\b(house|home)\s*(and|&|n)?\s*land\b/.test(q) ||
      /\b(house\s*and\s*land|home\s*and\s*land)\s*package(s)?\b/.test(q) ||
      /^packages?$/.test(q);
    if (isGenericIntent) {
      const total = packageSuburbs.reduce((acc, s) => acc + s.count, 0);
      const adelaideCount = packageRegions
        .filter((r) => r.region === 'Adelaide' || r.region === 'Adelaide Hills')
        .reduce((acc, r) => acc + r.count, 0);
      const otherRegions = packageRegions.filter(
        (r) => r.region !== 'Adelaide' && r.region !== 'Adelaide Hills' && r.count > 0
      );
      return {
        kind: 'package_hub',
        count: adelaideCount > 0 ? adelaideCount : total,
        totalCount: total,
        otherRegions,
        to: '/house-and-land-adelaide',
      };
    }

    // 3) Fallback - for any non-trivial search that didn't match a suburb or
    //    a generic H&L phrase, still offer the H&L hub as a secondary path.
    //    This guarantees a one-click route into the catalog from every search.
    if (q.length >= 4) {
      const adelaideCount = packageRegions
        .filter((r) => r.region === 'Adelaide' || r.region === 'Adelaide Hills')
        .reduce((acc, r) => acc + r.count, 0);
      if (adelaideCount > 0) {
        return {
          kind: 'package_hub_fallback',
          count: adelaideCount,
          to: '/house-and-land-adelaide',
        };
      }
    }

    return null;
  }, [query, packageSuburbs, packageRegions]);

  // Display-home search surfacing. Fires when the query reads like a
  // display-home hunt ("display homes near Mawson Lakes", "display village
  // Munno Para"). If it names a suburb we have a display home in, deep-link
  // to that suburb's directory; otherwise route to the state/national hub.
  const displayHomeSuggestion = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 3 || displayHomeSuburbs.length === 0) return null;
    const hasDisplayIntent = /display\s*(home|homes|village|villages|centre|centres|center|centers)/.test(q)
      || /\bdisplays?\b/.test(q);
    const qNorm = q.replace(/\bmt\b/g, 'mount').replace(/\bst\b/g, 'saint');

    // Find a display suburb named in the query (longest name first).
    const sorted = [...displayHomeSuburbs].sort((a, b) => b.suburb.length - a.suburb.length);
    const subMatch = sorted.find((s) => {
      const n = s.suburb.toLowerCase();
      return n.length >= 4 && (q.includes(n) || qNorm.includes(n));
    });

    if (subMatch && (hasDisplayIntent || /\bnear\b/.test(q))) {
      const st = (subMatch.state || '').toLowerCase();
      return {
        kind: 'display_suburb',
        suburb: subMatch.suburb,
        state: subMatch.state,
        count: subMatch.count,
        builders: subMatch.builders || [],
        to: st
          ? `/display-homes/${st}?suburb=${encodeURIComponent(subMatch.suburb)}`
          : `/display-homes?suburb=${encodeURIComponent(subMatch.suburb)}`,
      };
    }

    if (hasDisplayIntent) {
      // Generic display-home intent, optionally naming a state.
      let to = '/display-homes';
      if (/\b(vic|victoria|melbourne)\b/.test(q)) to = '/display-homes/vic';
      else if (/\b(sa|south australia|adelaide)\b/.test(q)) to = '/display-homes/sa';
      const total = displayHomeSuburbs.reduce((a, s) => a + s.count, 0);
      return { kind: 'display_hub', count: total, to };
    }
    return null;
  }, [query, displayHomeSuburbs]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  // Rotate placeholder hint while the input is empty and unfocused.
  useEffect(() => {
    if (!useRotating) return;
    if (query) return;
    const id = setInterval(() => {
      setHintIdx((i) => (i + 1) % activeHints.length);
    }, 2400);
    return () => clearInterval(id);
  }, [useRotating, query, activeHints.length]);

  // Reset to the primary hint whenever the active set changes (stage/state).
  useEffect(() => { setHintIdx(0); }, [activeHints]);

  // MiniMatcher state, when the user clicks a matcher row in the
  // dropdown, we open the popup instead of navigating.
  const [matcherOpen, setMatcherOpen] = useState(null);   // { category, sourcePlacement }
  // BuildTypePopup state, when the user clicks a build-type row.
  const [buildTypeOpen, setBuildTypeOpen] = useState(null); // chip {name, href, iconSrc}

  const openBuildType = (suggestion) => {
    // Find the matching chip in the buildingTypes inventory so the
    // popup gets the right icon + display name. Fall back to a
    // minimal synthetic chip if the popup config exists but no chip
    // is registered.
    const chip = buildingTypes.find((t) => t.href === suggestion.buildType)
      || { name: suggestion.title, href: suggestion.buildType, icon: null };
    setOpen(false);
    setBuildTypeOpen({ name: chip.name, href: chip.href, iconSrc: chip.icon });
    pushRecentSearch(query);
    setRecents(getRecentSearches());
    if (onNavigate) onNavigate();
  };

  const openMatcher = (suggestion) => {
    if (suggestion.linkTo) {
      go(suggestion.linkTo);
      return;
    }
    setOpen(false);
    setMatcherOpen({
      category: suggestion.category,
      heading: suggestion.label || suggestion.title || null,
      sourcePlacement: `home-search-${suggestion.category.replace(':', '-')}`,
    });
    pushRecentSearch(query);
    setRecents(getRecentSearches());
    if (onNavigate) onNavigate();
  };

  const go = (to, opts = {}) => {
    if (query && query.length >= 2 && !opts.skipRecord) {
      pushRecentSearch(query);
      setRecents(getRecentSearches());
    }
    setOpen(false);
    setQuery("");
    if (onNavigate) onNavigate();
    navigate(to);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (open && suggestions[highlight]) {
      const sel = suggestions[highlight];
      if (sel.kind === 'matcher') {
        openMatcher(sel);
      } else if (sel.kind === 'build_type') {
        openBuildType(sel);
      } else {
        go(sel.to);
      }
      return;
    }
    // Submit-without-suggestion: send to /search?q= for a full results page.
    const q = (query || '').trim();
    if (q.length >= 2) {
      pushRecentSearch(q);
      setRecents(getRecentSearches());
      setOpen(false);
      setQuery("");
      if (onNavigate) onNavigate();
      navigate(`/search?q=${encodeURIComponent(q)}`);
      return;
    }
    const intent = resolveIntent(query);
    if (intent) go(intent.to);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Load recent searches once on mount.
  useEffect(() => { setRecents(getRecentSearches()); }, []);

  // Fetch trending (cached 5 min). Don't block render - fire after mount.
  useEffect(() => {
    let alive = true;
    fetchTrendingSearches().then((list) => { if (alive) setTrending(list); });
    return () => { alive = false; };
  }, []);

  // Global "/" shortcut to focus search (skip when typing in an input).
  useEffect(() => {
    const onGlobalKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
      setOpen(true);
    };
    window.addEventListener('keydown', onGlobalKey);
    return () => window.removeEventListener('keydown', onGlobalKey);
  }, []);

  // Analytics - debounced log of every meaningful query.
  useEffect(() => {
    if (query && query.length >= 2) {
      logSearchQuery({ query, resultCount: suggestions.length, source: variant });
    }
  }, [query, suggestions.length, variant]);

  // Voice input wiring.
  const toggleVoice = () => {
    if (voiceOn) {
      try { recogRef.current?.stop(); } catch { /* ignore */ }
      setVoiceOn(false);
      return;
    }
    setVoiceOn(true);
    setOpen(true);
    recogRef.current = startVoiceInput({
      onResult: (transcript, isFinal) => {
        setQuery(transcript);
        setHighlight(0);
        if (isFinal) setVoiceOn(false);
      },
      onError: () => setVoiceOn(false),
      onEnd: () => setVoiceOn(false),
    });
  };

  // Style variants
  const isHero = variant === "hero";
  const isHeader = variant === "header";

  const wrapperCls = isHero
    ? "w-full max-w-[820px] mx-auto"
    : isHeader
      ? "w-full max-w-[420px]"
      : "w-full max-w-[600px]";

  const inputBoxCls = isHero
    ? "flex items-center gap-2 sm:gap-3 bg-white/95 backdrop-blur-sm border border-white/20 rounded-full pl-4 sm:pl-6 pr-1.5 sm:pr-2 py-1.5 sm:py-2 shadow-[0_8px_30px_rgba(0,0,0,0.18)]"
    : isHeader
      ? "flex items-center gap-2 bg-white border border-gray-200 rounded-full pl-4 pr-1 py-1"
      : "flex items-center gap-3 bg-white border border-gray-200 rounded-full pl-5 pr-2 py-2";

  const inputCls = isHero
    ? "w-full bg-transparent outline-none text-[16px] text-[#1d1d1f] placeholder:text-gray-500 py-2"
    : isHeader
      ? "w-full bg-transparent outline-none text-[16px] text-[#1d1d1f] placeholder:text-gray-500 py-1.5"
      : "w-full bg-transparent outline-none text-[16px] text-[#1d1d1f] placeholder:text-gray-500 py-2";

  const buttonCls = isHero
    ? "shrink-0 inline-flex items-center justify-center gap-2 gradient-orange-bg text-white w-11 h-11 sm:w-auto sm:h-auto sm:px-6 sm:py-2.5 rounded-full text-[15px] font-medium hover:opacity-95 transition-opacity shadow-[0_4px_14px_rgba(245,158,11,0.4)]"
    : isHeader
      ? "shrink-0 inline-flex items-center justify-center gradient-orange-bg text-white w-9 h-9 rounded-full hover:opacity-95 transition-opacity"
      : "shrink-0 inline-flex items-center justify-center gap-2 gradient-orange-bg text-white px-5 py-2 rounded-full text-[14px] font-medium hover:opacity-95 transition-opacity";

  const showVoice = supportsVoiceInput() && (isHero || variant === 'inline');

  return (
    <div ref={wrapperRef} className={`relative ${wrapperCls}`}>
      <form onSubmit={handleSubmit} className={inputBoxCls} data-testid="intent-search-form">
        <Search className={isHero ? "w-5 h-5 text-gray-500 shrink-0" : "w-4 h-4 text-gray-500 shrink-0"} />
        <div className="relative flex-1 min-w-0">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
            onFocus={() => { setOpen(true); setFocused(true); }}
            onBlur={() => setFocused(false)}
            onKeyDown={handleKeyDown}
            placeholder={useRotating ? '' : activePlaceholder}
            className={inputCls}
            aria-label="Search BuildPilot"
            data-testid="intent-search-input"
            autoComplete="off"
          />
          {showAnimatedHint && (
            <div
              className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center overflow-hidden min-w-0"
              aria-hidden="true"
              data-testid="intent-search-animated-hint"
            >
              {!isPrimaryHint && (
                <span className="shrink-0 text-[13px] sm:text-[16px] text-gray-500 whitespace-nowrap">Try:&nbsp;</span>
              )}
              <span
                key={hintIdx}
                className="text-[13px] sm:text-[16px] text-gray-500 truncate intent-hint-rotate"
              >
                {currentHint}
              </span>
            </div>
          )}
        </div>
        {/* Clear button when user has typed something */}
        {query && !voiceOn && (
          <button
            type="button"
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            className="shrink-0 w-7 h-7 inline-flex items-center justify-center text-gray-400 hover:text-[#1d1d1f] rounded-full"
            aria-label="Clear search"
            data-testid="intent-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {/* Voice mic */}
        {showVoice && (
          <button
            type="button"
            onClick={toggleVoice}
            className={`shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-full transition-colors ${
              voiceOn ? 'bg-[var(--bp-color-surface-tinted)] text-[var(--bp-color-ink)] animate-pulse' : 'text-gray-500 hover:text-[#1d1d1f] hover:bg-gray-100'
            }`}
            aria-label={voiceOn ? "Stop voice input" : "Start voice input"}
            title="Search with voice"
            data-testid="intent-search-voice"
          >
            {voiceOn ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
        <button type="submit" className={buttonCls} data-testid="intent-search-submit" aria-label="Search">
          {isHeader ? (
            <ArrowRight className="w-4 h-4" />
          ) : isHero ? (
            <>
              <span className="hidden sm:inline">Search</span>
              <ArrowRight className="w-4 h-4" />
            </>
          ) : (
            <><span>Search</span><ArrowRight className="w-4 h-4" /></>
          )}
        </button>
        {/* "Get Started Today" - hero variant only, hidden when the visitor is
            already signed in. Positioned to the RIGHT of the Search button
            so it reads as a parallel choice ("search OR start free"). Same
            outer search-bar size preserved - just an extra inline pill. */}
        {isHero && !marketingAuth.loading && !marketingAuth.isLoggedIn && (
          <button
            type="button"
            onClick={() => authModal.open('signup')}
            className="shrink-0 hidden md:inline-flex items-center bp-orange-glow-bg text-white px-4 py-2.5 rounded-full text-[13px] lg:text-[14px] font-semibold hover:opacity-95 transition-opacity shadow-[0_4px_14px_rgba(245,166,35,0.4)] whitespace-nowrap"
            data-testid="hero-start-today"
            aria-label="Start your build today"
          >
            Get Started Today
          </button>
        )}
      </form>

      {/* Dropdown - appears whenever the bar is open.
          Scroll model: the dropdown root is the scroll container, capped
          at min(70vh, 640px) so it never exceeds the viewport on either
          mobile or desktop. All inner sections (recents / trending /
          popular / results / trailing prompts / no-results card) render
          inside this single scroll surface, so the user can always
          reach any section regardless of how many results are loaded.
          z-[100] keeps the dropdown above the scrim (z-[90]), above
          floating CTAs, the cookie banner, the sticky Find a Builder
          chip, and the Header's dropdown menus. */}
      {open && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white rounded-2xl border border-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.12)] overflow-y-auto overscroll-contain z-[100] max-h-[min(70vh,640px)]"
          data-testid="intent-search-suggestions"
        >
          {/* ── Empty state: recents + trending + popular ── */}
          {!query && (
            <>
              {recents.length > 0 && (
                <div className="border-b border-gray-100">
                  <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500 inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> Recent searches
                    </span>
                    <button
                      type="button"
                      onClick={() => { clearRecentSearches(); setRecents([]); }}
                      className="text-[11px] text-gray-400 hover:text-[#1d1d1f] transition-colors"
                      data-testid="intent-search-clear-recents"
                    >
                      Clear
                    </button>
                  </div>
                  <ul className="pb-2">
                    {recents.map((r, i) => (
                      <li key={`r-${i}`}>
                        <button
                          type="button"
                          onClick={() => { setQuery(r); inputRef.current?.focus(); }}
                          className="w-full text-left px-5 py-2 text-[14px] text-[#1d1d1f] hover:bg-gray-50 inline-flex items-center gap-3"
                          data-testid={`intent-recent-${i}`}
                        >
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          {r}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {trending.length > 0 && (
                <div className="border-b border-gray-100">
                  <div className="px-5 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500 inline-flex items-center gap-1.5">
                    <TrendingUp className="w-3 h-3" /> Trending this week
                  </div>
                  <ul className="pb-2">
                    {trending.map((t, i) => (
                      <li key={`t-${i}`}>
                        <button
                          type="button"
                          onClick={() => { setQuery(t); inputRef.current?.focus(); }}
                          className="w-full text-left px-5 py-2 text-[14px] text-[#1d1d1f] hover:bg-gray-50 inline-flex items-center gap-3"
                          data-testid={`intent-trending-${i}`}
                        >
                          <TrendingUp className="w-3.5 h-3.5 text-[#a55c2a]" />
                          {t}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="px-5 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">
                Popular searches
              </div>
            </>
          )}

          {/* ── Results list ──
              No `overflow-y-auto` or `max-h` here. The dropdown root is
              the single scroll container so users can reach Popular
              Searches above and the trailing CTA / no-results card
              below without sections getting clipped. */}
          {/* Product-intent tile - routes shopping queries into /products.
              Standalone so it shows even when nothing else matches. */}
          {query && productSuggestion && (
            <ul className="py-2 border-b border-gray-100">
              <li>
                <button
                  type="button"
                  onClick={() => go(productSuggestion.to)}
                  className="w-full text-left px-5 py-3 transition-colors bg-gradient-to-r from-[#eef7ff] to-transparent border-l-2 border-[#1d8efe] hover:from-[#dceeff]"
                  data-testid="intent-search-product-prompt"
                >
                  <div className="flex items-start gap-3">
                    <ShoppingBag className="w-4 h-4 shrink-0 mt-1 text-[#1d8efe]" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[14px] font-semibold text-[#1d1d1f] leading-tight">
                          {productSuggestion.label}
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-full text-[#1d8efe] bg-[#1d8efe]/10">
                          See products
                        </span>
                      </div>
                      <p className="text-[12.5px] text-gray-600 leading-[1.45] mt-1">
                        Browse the range we've matched from our retail partners — buy direct at their price.
                      </p>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1" />
                  </div>
                </button>
              </li>
            </ul>
          )}

          {query && displayHomeSuggestion && (
            <ul className="py-2 border-b border-gray-100">
              <li>
                <button
                  type="button"
                  onClick={() => go(displayHomeSuggestion.to)}
                  className="w-full text-left px-5 py-3 transition-colors bg-gradient-to-r from-[#eefaf3] to-transparent border-l-2 border-[#1f9d57] hover:from-[#dcf3e7]"
                  data-testid="intent-search-display-home-prompt"
                >
                  <div className="flex items-start gap-3">
                    <Home className="w-4 h-4 shrink-0 mt-1 text-[#1f9d57]" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[14px] font-semibold text-[#1d1d1f] leading-tight">
                          {displayHomeSuggestion.kind === 'display_suburb'
                            ? `${displayHomeSuggestion.count} display home${displayHomeSuggestion.count === 1 ? '' : 's'} near ${displayHomeSuggestion.suburb}`
                            : `Browse ${displayHomeSuggestion.count} display homes open to visit`}
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-full text-[#1f9d57] bg-[#1f9d57]/10">
                          Display homes
                        </span>
                      </div>
                      <p className="text-[12.5px] text-gray-600 leading-[1.45] mt-1">
                        {displayHomeSuggestion.kind === 'display_suburb'
                          ? `${(displayHomeSuggestion.builders || []).slice(0, 3).join(', ')}${displayHomeSuggestion.builders && displayHomeSuggestion.builders.length > 3 ? ' and more' : ''} — addresses, opening hours and directions.`
                          : 'Addresses, opening hours and photos, sourced from each builder\u2019s own website.'}
                      </p>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1" />
                  </div>
                </button>
              </li>
            </ul>
          )}

          {suggestions.length > 0 && (
            <ul className="py-2">
              {/* Trigger 4 - Live H&L package match. Highest-priority tile when
                  the user types a suburb we have inventory in, or a "house &
                  land" intent phrase. */}
              {packageSuggestion && (
                <li>
                  <button
                    type="button"
                    onClick={() => go(packageSuggestion.to)}
                    className="w-full text-left px-5 py-3 transition-colors bg-gradient-to-r from-[#1a1a1c] to-transparent border-l-2 border-[#febd59] hover:from-[#252528]"
                    data-testid="intent-search-package-prompt"
                  >
                    <div className="flex items-start gap-3">
                      <Home className="w-4 h-4 shrink-0 mt-1 text-[#febd59]" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[14px] font-semibold text-white leading-tight">
                            {packageSuggestion.kind === 'package_suburb'
                              ? `${packageSuggestion.count} live Home & Land package${packageSuggestion.count === 1 ? '' : 's'} in ${packageSuggestion.suburb}`
                              : `${packageSuggestion.count} live Home & Land package${packageSuggestion.count === 1 ? '' : 's'} in Adelaide`}
                          </span>
                          <span className="text-[10px] font-semibold uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-full text-[#1d1d1f] bg-[#febd59]">
                            Live
                          </span>
                        </div>
                        <p className="text-[12.5px] text-white/55 leading-[1.45] mt-1">
                          {packageSuggestion.kind === 'package_hub' && packageSuggestion.otherRegions && packageSuggestion.otherRegions.length > 0
                            ? `Plus ${packageSuggestion.otherRegions.reduce((a, r) => a + r.count, 0)} also live across ${packageSuggestion.otherRegions.map((r) => r.region).join(', ')}. Full details in the BuildPilot App.`
                            : packageSuggestion.kind === 'package_hub_fallback'
                            ? 'Browse the full live catalog while you search. Beds, baths, land size at a glance.'
                            : 'View beds, baths and land size now. Full details in the BuildPilot App.'}
                        </p>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-white/60 shrink-0 mt-1" />
                    </div>
                  </button>
                </li>
              )}
              {showDecisionPrompt && !noResults && saConfirmed && (
                <li>
                  <button
                    type="button"
                    onClick={() => go('/one-on-one-support')}
                    className="w-full text-left px-5 py-3 transition-colors bg-gradient-to-r from-[#fef6ee] to-transparent border-l-2 border-[#ff8c28] hover:bg-[#fdecd9]"
                    data-testid="intent-search-decision-prompt"
                  >
                    <div className="flex items-start gap-3">
                      <LifeBuoy className="w-4 h-4 shrink-0 mt-1 text-[#a55c2a]" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[14px] font-semibold text-[#1d1d1f] leading-tight">
                            Sounds like a decision moment.
                          </span>
                          <span className="text-[10px] font-semibold uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-full text-[#a55c2a] bg-[#a55c2a]/10">
                            Real human
                          </span>
                        </div>
                        <p className="text-[12.5px] text-gray-600 leading-[1.45] mt-1">
                          We can walk you through it - One on One Support.
                        </p>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1" />
                    </div>
                  </button>
                </li>
              )}
              {suggestions.map((s, i) => {
                const meta = KIND_META[s.kind] || KIND_META.page;
                const Icon = meta.icon;
                const isRich = s.kind !== 'page' || s.subtitle;
                const isCompound = s.kind === 'compound';
                const isMatcher = s.kind === 'matcher';
                const isBuildType = s.kind === 'build_type';
                const isWorkspaceTool = s.kind === 'workspace-tool';
                // 2026-feb: Live catalog rows (package/design/land)
                // reuse the workspace-tool visual treatment so the
                // thumbnail (facade / floor-plan / hero image) renders
                // to the LEFT of the title in a 36×36 square.
                const isCatalog = s.kind === 'package' || s.kind === 'design' || s.kind === 'land' || s.kind === 'business';
                const showImageIcon = (isWorkspaceTool || isCatalog) && s.iconUrl;
                return (
                  <React.Fragment key={(s.to || s.category || s.buildType || 'm') + i}>
                    {i === adInsertIndex && sponsoredAnswer && (
                      <li key="sponsored-answer" data-testid="intent-search-sponsored-slot">
                        <SponsoredSearchAnswer ad={sponsoredAnswer} />
                      </li>
                    )}
                    <li>
                    <button
                      type="button"
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => {
                        if (isBuildType) openBuildType(s);
                        else if (isMatcher) openMatcher(s);
                        else go(s.to);
                      }}
                      className={`w-full text-left px-5 py-2.5 transition-colors ${
                        isCompound
                          ? 'bg-gradient-to-r from-[#fff8ef] to-transparent border-l-2 border-[#febd59]'
                          : isBuildType
                          ? 'bg-gradient-to-r from-[#f5f3ff] to-transparent border-l-2 border-[#7c3aed]'
                          : isMatcher
                          ? 'bg-gradient-to-r from-[#eef7ff] to-transparent border-l-2 border-[#1d8efe]'
                          : highlight === i ? 'bg-gray-50' : 'hover:bg-gray-50'
                      } ${highlight === i && (isCompound || isMatcher || isBuildType) ? 'bg-[#dceeff]/60' : ''}`}
                      data-testid={`intent-suggestion-${i}`}
                    >
                      {isRich ? (
                        <div className="flex items-start gap-3">
                          {s.kind === 'business' ? (
                            <BusinessLogoTile
                              title={s.title}
                              logoUrl={s.iconUrl}
                              brandColor={s.brandColor}
                            />
                          ) : s.logo ? (
                            <span className="w-9 h-9 shrink-0 mt-0.5 rounded-md border border-gray-100 bg-white flex items-center justify-center overflow-hidden">
                              <img
                                src={s.logo}
                                alt=""
                                aria-hidden="true"
                                className="max-w-[80%] max-h-[80%] object-contain"
                                loading="lazy"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            </span>
                          ) : showImageIcon ? (
                            <img
                              src={s.iconUrl}
                              alt=""
                              aria-hidden="true"
                              className={`w-9 h-9 shrink-0 object-cover mt-0.5 ${isCatalog ? 'rounded-md border border-gray-100' : 'object-contain'}`}
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <Icon className="w-4 h-4 shrink-0 mt-1" style={{ color: meta.accent }} />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[14px] font-semibold text-[#1d1d1f] leading-tight">
                                <HighlightedText text={s.title} query={query} />
                              </span>
                              <span
                                className="text-[10px] font-semibold uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-full"
                                style={{ color: meta.accent, background: `${meta.accent}1a` }}
                              >
                                {s.badge || meta.label}
                              </span>
                              {isWorkspaceTool && s.premium && (
                                <span className="text-[9.5px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded-full text-white bg-[#f5a623]">
                                  Premium
                                </span>
                              )}
                              {s.subtitle && !isWorkspaceTool && (
                                <span className="text-[10px] text-gray-500 truncate">{s.subtitle}</span>
                              )}
                            </div>
                            {s.snippet && !isWorkspaceTool && (
                              <p className="text-[12.5px] text-gray-600 leading-[1.45] mt-1 line-clamp-2">
                                {isCatalog ? s.snippet : <HighlightedText text={s.snippet} query={query} />}
                              </p>
                            )}
                            {isWorkspaceTool && (
                              <p className="text-[12.5px] text-gray-600 leading-[1.45] mt-0.5">
                                Open in your BuildPilot dashboard
                              </p>
                            )}
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-3 text-[14px]">
                          <span className={`flex items-center gap-3 ${
                            highlight === i ? 'text-[#1d1d1f]' : 'text-gray-700'
                          }`}>
                            <Search className="w-3.5 h-3.5 text-gray-400" />
                            <HighlightedText text={s.title} query={query} />
                          </span>
                          <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                        </div>
                      )}
                    </button>
                  </li>
                  </React.Fragment>
                );
              })}
            </ul>
          )}

          {/* Trigger 2 - compound-query trailing prompt. After results for
              queries like "builder in Munno Para" or "double storey under
              $700k", append a soft "want a hand?" card. Doesn't replace
              results, sits below them. */}
          {showCompoundPrompt && saConfirmed && (
            <button
              type="button"
              onClick={() => go('/one-on-one-support')}
              className="w-full text-left px-5 py-3.5 border-t border-gray-100 bg-gradient-to-r from-[#fafafa] to-white hover:from-[#fef6ee] transition-colors"
              data-testid="intent-search-compound-prompt"
            >
              <div className="flex items-start gap-3">
                <LifeBuoy className="w-4 h-4 shrink-0 mt-0.5 text-[#a55c2a]" />
                <div className="flex-1">
                  <p className="text-[13px] text-[#1d1d1f] leading-[1.5]">
                    <span className="font-semibold">Picking between a few options?</span>{' '}
                    <span className="text-gray-600">That's literally what we do all day. Get an unbiased compare in One on One Support.</span>
                  </p>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1" />
              </div>
            </button>
          )}

          {/* ── No-results state with helpful redirects ── */}
          {query && noResults && !productSuggestion && (
            <div className="px-5 py-5 border-t border-gray-100">
              <p className="text-[13px] text-[#1d1d1f] mb-3">
                <span className="font-semibold">No direct match for "{query}".</span>{' '}
                <span className="text-gray-600">Download the BuildPilot app for self-service tools, or get One on One Support.</span>
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="https://buildpilot.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-[#1d1d1f] text-white px-3.5 py-1.5 rounded-full text-[12px] font-medium hover:bg-black transition-colors"
                  data-testid="intent-search-app-cta"
                >
                  Download BuildPilot App <ArrowRight className="w-3 h-3" />
                </a>
                <a
                  href="/one-on-one-support"
                  className="inline-flex items-center gap-1.5 border border-gray-300 text-[#1d1d1f] px-3.5 py-1.5 rounded-full text-[12px] font-medium hover:bg-gray-50 transition-colors"
                  data-testid="intent-search-1on1-cta"
                >
                  Talk to a real human
                </a>
              </div>
            </div>
          )}

          {/* ── Footer ── */}
          {query && (
            <div className="border-t border-gray-100 px-5 py-2.5 text-[11px] text-gray-500 flex items-center justify-between">
              <span>
                <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-medium">↑↓</kbd> to navigate ·
                <kbd className="ml-1 px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-medium">Enter</kbd> to open
              </span>
              <a
                href={`/search?q=${encodeURIComponent(query)}`}
                className="font-medium text-[#1d8efe] hover:text-[#1d1d1f] transition-colors"
                data-testid="intent-search-see-all"
              >
                See all results →
              </a>
            </div>
          )}

          {/* Trigger 3 - quiet permanent footer line in the empty-state
              dropdown. Sits there when no query is typed. Single line, light
              grey, never shouts. Apple-style "or call 133-622" vibe. */}
          {!query && (
            <a
              href="/one-on-one-support"
              onClick={(e) => { e.preventDefault(); go('/one-on-one-support'); }}
              className="block border-t border-gray-100 px-5 py-3 text-[12px] text-gray-500 hover:bg-gray-50 hover:text-[#1d1d1f] transition-colors"
              data-testid="intent-search-empty-1on1"
            >
              <span className="text-gray-400">Rather talk it through?</span>{' '}
              <span className="text-[#1d8efe] font-medium">Talk to a real human →</span>
            </a>
          )}
        </div>
      )}
      {/* MiniMatcher mount, fires when user clicks a matcher row
          in the search dropdown. Rendered last so the popup portals
          to body z-index 99999, above the search dropdown. */}
      <MiniMatcher
        open={!!matcherOpen}
        onClose={() => setMatcherOpen(null)}
        category={matcherOpen?.category}
        heading={matcherOpen?.heading}
        sourcePlacement={matcherOpen?.sourcePlacement || 'home-search'}
        sourcePage="/"
      />
      {buildTypeOpen && (
        <BuildTypePopup
          chip={buildTypeOpen}
          onClose={() => setBuildTypeOpen(null)}
        />
      )}
    </div>
  );
};

export default IntentSearchBar;

